import React, { useEffect, useRef, useState } from 'react';
import { Eye, Trash2, FolderPlus, Edit3, Scissors, Clipboard, CheckCircle, Plus, Ban, Tags, ChevronRight } from 'lucide-react';

export interface ContextMenuState {
  x: number;
  y: number;
  visible: boolean;
  type: 'ASSET' | 'FOLDER' | 'NAS_FOLDER' | 'BACKGROUND';
  targetId?: string; // asset ID or folder path
}

interface ContextMenuProps {
  state: ContextMenuState;
  onAction: (action: string) => void;
  clipboardFolder: { path: string; action: 'CUT' | 'COPY' } | null;
  canEditSingleAsset?: boolean;
  tagMenu?: {
    enabled: boolean;
    addOptions: Array<{ key: string; label: string }>;
    addTypeOptions?: Array<{ typeKey: string; typeLabel: string; options: Array<{ key: string; label: string }> }>;
    removeOptions: Array<{ key: string; label: string }>;
    manageAction: 'manageTags' | 'manageNasTags';
  };
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  state,
  onAction,
  clipboardFolder,
  canEditSingleAsset = false,
  tagMenu,
}) => {
  const [showTagSubmenu, setShowTagSubmenu]       = useState(false);
  const [showAddSubmenu, setShowAddSubmenu]       = useState(false);
  const [showRemoveSubmenu, setShowRemoveSubmenu] = useState(false);
  const [activeAddTab, setActiveAddTab]           = useState<string>('產品');
  const [addSearch, setAddSearch]                 = useState('');
  const [removeSearch, setRemoveSearch]           = useState('');

  const tagHideTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addHideTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const removeHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = (ref: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) => {
    if (ref.current) { clearTimeout(ref.current); ref.current = null; }
  };

  // ── Level-1: tag root ────────────────────────────────────────────────────
  const openTag = () => { clearTimer(tagHideTimer); setShowTagSubmenu(true); };
  const scheduleCloseTag = () => {
    tagHideTimer.current = setTimeout(() => {
      setShowTagSubmenu(false);
      setShowAddSubmenu(false);
      setShowRemoveSubmenu(false);
    }, 100);
  };

  // ── Level-2: "新增" ──────────────────────────────────────────────────────
  const openAdd = () => {
    clearTimer(addHideTimer);
    clearTimer(removeHideTimer);
    setShowAddSubmenu(true);
    setShowRemoveSubmenu(false);
    if (tagMenu?.addTypeOptions?.length) {
      const groups = tagMenu.addTypeOptions;
      if (!groups.some((g) => g.typeKey === activeAddTab)) {
        const preferred = groups.find((g) => g.typeKey === '產品')?.typeKey ?? groups[0]?.typeKey;
        if (preferred) setActiveAddTab(preferred);
      }
    }
  };
  const scheduleCloseAdd = () => {
    addHideTimer.current = setTimeout(() => setShowAddSubmenu(false), 100);
  };

  // ── Level-2: "移除" ──────────────────────────────────────────────────────
  const openRemove = () => {
    clearTimer(removeHideTimer);
    clearTimer(addHideTimer);
    setShowRemoveSubmenu(true);
    setShowAddSubmenu(false);
  };
  const scheduleCloseRemove = () => {
    removeHideTimer.current = setTimeout(() => setShowRemoveSubmenu(false), 100);
  };

  // Reset on menu close
  useEffect(() => {
    if (!state.visible) {
      clearTimer(tagHideTimer);
      clearTimer(addHideTimer);
      clearTimer(removeHideTimer);
      setShowTagSubmenu(false);
      setShowAddSubmenu(false);
      setShowRemoveSubmenu(false);
      setActiveAddTab('產品');
      setAddSearch('');
      setRemoveSearch('');
    }
  }, [state.visible]);

  // Cleanup on unmount
  useEffect(() => () => {
    clearTimer(tagHideTimer);
    clearTimer(addHideTimer);
    clearTimer(removeHideTimer);
  }, []);

  if (!state.visible) return null;

  const canShowTagRoot = (state.type === 'ASSET' || state.type === 'NAS_FOLDER') && tagMenu?.enabled;
  const MAX_MENU_ITEMS = 20;

  const addTabGroups = (tagMenu?.addTypeOptions && tagMenu.addTypeOptions.length > 0)
    ? tagMenu.addTypeOptions
    : [{ typeKey: 'all', typeLabel: '全部', options: tagMenu?.addOptions || [] }];

  const resolvedActiveAddTab = addTabGroups.some((g) => g.typeKey === activeAddTab)
    ? activeAddTab
    : (addTabGroups.find((g) => g.typeKey === '產品')?.typeKey ?? addTabGroups[0]?.typeKey ?? 'all');

  const activeAddTabGroup = addTabGroups.find((g) => g.typeKey === resolvedActiveAddTab);

  const filteredActiveAddOptions = (activeAddTabGroup?.options || []).filter((tag) =>
    !addSearch.trim() ? true : tag.label.toLowerCase().includes(addSearch.trim().toLowerCase())
  );
  const visibleActiveAddOptions = filteredActiveAddOptions.slice(0, MAX_MENU_ITEMS);

  const filteredRemoveOptions = (tagMenu?.removeOptions || []).filter((tag) =>
    !removeSearch.trim() ? true : tag.label.toLowerCase().includes(removeSearch.trim().toLowerCase())
  );
  const visibleRemoveOptions = filteredRemoveOptions.slice(0, MAX_MENU_ITEMS);

  // Transparent bridge that fills the gap between trigger row and submenu panel.
  // Being a child of the trigger's wrapper div, it prevents onMouseLeave from
  // firing while the cursor is crossing the gap.
  const BRIDGE = 'w-1 self-stretch shrink-0';

  const renderTagRoot = () => {
    if (!canShowTagRoot || !tagMenu) return null;

    return (
      <div
        className="relative"
        onMouseEnter={openTag}
        onMouseLeave={scheduleCloseTag}
      >
        <button className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-bold text-indigo-700 hover:bg-indigo-50 transition-colors">
          <span className="flex items-center gap-3"><Tags size={14} /> 標籤</span>
          <ChevronRight size={12} />
        </button>

        {showTagSubmenu && (
          <div className="absolute top-0 left-full flex items-start z-[210]">
            {/* transparent bridge — no gap, no stray mouse-leave */}
            <div className={BRIDGE} />

            {/* Level-2 submenu panel */}
            <div className="bg-white border border-slate-100 shadow-2xl rounded-xl py-2 min-w-[180px] -ml-2">

              {/* ── 新增 ──────────────────────────────────────────────── */}
              <div
                className="relative"
                onMouseEnter={openAdd}
                onMouseLeave={scheduleCloseAdd}
              >
                <button className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600">
                  <span>新增</span>
                  <ChevronRight size={11} />
                </button>

                {showAddSubmenu && (
                  <div className="absolute top-0 left-full flex items-start z-[220]">
                    <div className={BRIDGE} />
                    <div className="bg-white border border-slate-100 shadow-2xl rounded-xl py-2 min-w-[240px] max-h-[280px] overflow-y-auto -ml-2">
                      <div className="px-2 pb-2 flex items-center gap-1 border-b border-slate-100 mb-2">
                        {addTabGroups.map((group) => {
                          const active = resolvedActiveAddTab === group.typeKey;
                          return (
                            <button
                              key={`add-tab-${group.typeKey}`}
                              onMouseDown={(e) => { e.preventDefault(); setActiveAddTab(group.typeKey); }}
                              className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors ${
                                active ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              {group.typeLabel}
                            </button>
                          );
                        })}
                      </div>
                      <div className="px-2 pb-2">
                        <input
                          value={addSearch}
                          onChange={(e) => setAddSearch(e.target.value)}
                          placeholder="搜尋可新增標籤..."
                          className="w-full px-2 py-1.5 text-[11px] bg-slate-50 border border-slate-200 rounded-md outline-none focus:ring-1 focus:ring-indigo-400/30"
                        />
                      </div>
                      {visibleActiveAddOptions.length > 0 ? (
                        visibleActiveAddOptions.map((tag) => (
                          <button
                            key={`add-${tag.key}`}
                            onClick={() => onAction(`tag:add:${encodeURIComponent(tag.key)}`)}
                            className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700"
                            title={tag.label}
                          >
                            {tag.label}
                          </button>
                        ))
                      ) : (
                        <p className="px-3 py-2 text-[11px] text-slate-400">沒有可新增的標籤</p>
                      )}
                      {filteredActiveAddOptions.length > MAX_MENU_ITEMS && (
                        <p className="px-3 pt-1 text-[10px] text-slate-400">
                          還有 {filteredActiveAddOptions.length - MAX_MENU_ITEMS} 筆，請輸入關鍵字縮小範圍
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ── 移除 ──────────────────────────────────────────────── */}
              <div
                className="relative"
                onMouseEnter={openRemove}
                onMouseLeave={scheduleCloseRemove}
              >
                <button className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold text-slate-700 hover:bg-rose-50 hover:text-rose-600">
                  <span>移除</span>
                  <ChevronRight size={11} />
                </button>

                {showRemoveSubmenu && (
                  <div className="absolute top-0 left-full flex items-start z-[220]">
                    <div className={BRIDGE} />
                    <div className="bg-white border border-slate-100 shadow-2xl rounded-xl py-2 min-w-[240px] max-h-[280px] overflow-y-auto -ml-2">
                      <div className="px-2 pb-2">
                        <input
                          value={removeSearch}
                          onChange={(e) => setRemoveSearch(e.target.value)}
                          placeholder="搜尋可移除標籤..."
                          className="w-full px-2 py-1.5 text-[11px] bg-slate-50 border border-slate-200 rounded-md outline-none focus:ring-1 focus:ring-rose-400/30"
                        />
                      </div>
                      {visibleRemoveOptions.length > 0 ? (
                        visibleRemoveOptions.map((tag) => (
                          <button
                            key={`remove-${tag.key}`}
                            onClick={() => onAction(`tag:remove:${encodeURIComponent(tag.key)}`)}
                            className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-rose-50 hover:text-rose-700"
                            title={tag.label}
                          >
                            {tag.label}
                          </button>
                        ))
                      ) : (
                        <p className="px-3 py-2 text-[11px] text-slate-400">目前範圍沒有可移除標籤</p>
                      )}
                      {filteredRemoveOptions.length > MAX_MENU_ITEMS && (
                        <p className="px-3 pt-1 text-[10px] text-slate-400">
                          還有 {filteredRemoveOptions.length - MAX_MENU_ITEMS} 筆，請輸入關鍵字縮小範圍
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="h-[1px] bg-slate-100 my-1 mx-2" />
              <button
                onClick={() => onAction(tagMenu.manageAction)}
                className="w-full text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
              >
                標籤管理
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="fixed z-[200] bg-white border border-slate-100 shadow-2xl rounded-2xl py-2 min-w-[200px] animate-in fade-in zoom-in duration-150"
      style={{ top: state.y, left: state.x }}
      onClick={(e) => e.stopPropagation()}
    >
      {state.type === 'ASSET' && (
        <>
          {renderTagRoot()}
          <div className="h-[1px] bg-slate-100 my-1 mx-2" />
          <button
            onClick={() => onAction('view')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
          >
            <Eye size={14} /> 瀏覽內容
          </button>
          {canEditSingleAsset && (
            <button
              onClick={() => onAction('edit')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
            >
              <Edit3 size={14} /> 編輯
            </button>
          )}
          <button
            onClick={() => onAction('cut')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
          >
            <Scissors size={14} /> 剪下
          </button>
          <button
            onClick={() => onAction('copy')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
          >
            <Clipboard size={14} /> 複製
          </button>
          <div className="h-[1px] bg-slate-100 my-1 mx-2" />
          <button
            onClick={() => onAction('blockFolder')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-amber-700 hover:bg-amber-50 transition-colors"
          >
            <Ban size={14} /> 封鎖此目錄
          </button>
          <button
            onClick={() => onAction('blockFile')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-amber-700 hover:bg-amber-50 transition-colors"
          >
            <Ban size={14} /> 封鎖此檔案
          </button>
          <div className="h-[1px] bg-slate-100 my-1 mx-2" />
          <button
            onClick={() => onAction('delete')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 size={14} /> 刪除
          </button>
        </>
      )}

      {state.type === 'NAS_FOLDER' && (
        <>
          {renderTagRoot()}
          <div className="h-[1px] bg-slate-100 my-1 mx-2" />
          <button
            onClick={() => onAction('clearNasFilter')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <Eye size={14} /> 清除此目錄篩選
          </button>
        </>
      )}

      {state.type === 'FOLDER' && (
        <>
          <button
            onClick={() => onAction('addFolder')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
          >
            <FolderPlus size={14} /> 新增子資料夾
          </button>
          <button
            onClick={() => onAction('rename')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
          >
            <Edit3 size={14} /> 重新命名
          </button>
          <div className="h-[1px] bg-slate-100 my-1 mx-2" />
          <button
            onClick={() => onAction('cut')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
          >
            <Scissors size={14} /> 剪下
          </button>
          <button
            onClick={() => onAction('paste')}
            disabled={!clipboardFolder}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Clipboard size={14} /> 貼上項目
          </button>
          <div className="h-[1px] bg-slate-100 my-1 mx-2" />
          <button
            onClick={() => onAction('deleteFolder')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 size={14} /> 刪除資料夾
          </button>
        </>
      )}

      {state.type === 'BACKGROUND' && (
        <>
          <button
            onClick={() => onAction('selectAll')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
          >
            <CheckCircle size={14} /> 全選項目
          </button>
          <button
            onClick={() => onAction('upload')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
          >
            <Plus size={14} /> 上傳素材
          </button>
        </>
      )}
    </div>
  );
};

export default ContextMenu;
